import * as React from 'react';
import { OverwhelmedOverlayDispatch, OverwhelmedOverlayDispatchAction, OverwhelmedOverlayState } from './types';
import useOverwhelmedOverlayData from './useOverwhelmedOverlayData';
import { useFeatureToggle } from '@ids/appshell/hooks';
import { USER_SUBMITTED_OVERWHELMED_FORM } from '@ids/components/OverwhelmedForm/OverwhelmedForm';
import { useIdsGaTracking } from '@ids/hooks';
import { useCookies } from '@ids/context/Cookies';

export const initialState = { isOverwhelmedOverlayOpen: false, isThankYouNoteOpen: false, formData: null };

const appReducer = (state: OverwhelmedOverlayState, action: OverwhelmedOverlayDispatchAction) => {
    switch (action.type) {
        case 'SHOW_OVERWHELMED_OVERLAY':
            return {
                ...state,
                isOverwhelmedOverlayOpen: true,
            };

        case 'HIDE_OVERWHELMED_OVERLAY':
            return {
                ...state,
                isOverwhelmedOverlayOpen: false,
            };

        case 'SHOW_THANK_YOU_NOTE':
            return {
                ...state,
                isThankYouNoteOpen: true,
            };

        case 'HIDE_THANK_YOU_NOTE':
            return {
                ...state,
                isThankYouNoteOpen: false,
            };

        default:
            return state;
    }
};

const OverwhelmedOverlayStateContext = React.createContext<OverwhelmedOverlayState>(initialState);

const OverwhelmedOverlayDispatchContext = React.createContext<OverwhelmedOverlayDispatch>({
    hideOverwhelmedOverlay: () => null,
    showOverwhelmedOverlay: () => null,
    showThankYouNote: () => null,
    hideThankYouNote: () => null,
});

const OverwhelmedOverlayProvider = ({ children }: { children: React.ReactNode }) => {
    const { trackIdsEvent } = useIdsGaTracking();
    const { p100IdsOverwhelmedOverlay } = useFeatureToggle();
    const [state, dispatch] = React.useReducer(appReducer, initialState);
    const overwhelmedOverlayData = useOverwhelmedOverlayData();
    const [cookies, , removeCookie] = useCookies([USER_SUBMITTED_OVERWHELMED_FORM], {
        path: '/',
        maxAge: 60 * 60 * 24 * 7,
    });

    const userSubmittedOverwhelmedForm = React.useMemo(
        () => cookies?.get(USER_SUBMITTED_OVERWHELMED_FORM) === 'true',
        [cookies]
    );

    React.useEffect(() => {
        if (p100IdsOverwhelmedOverlay && userSubmittedOverwhelmedForm) {
            dispatch({ type: 'SHOW_THANK_YOU_NOTE' });
        }
    }, [p100IdsOverwhelmedOverlay, userSubmittedOverwhelmedForm]);

    React.useEffect(() => {
        if (!p100IdsOverwhelmedOverlay) {
            removeCookie(USER_SUBMITTED_OVERWHELMED_FORM);
        }
    }, [p100IdsOverwhelmedOverlay, removeCookie]);

    const memoizedState = React.useMemo(
        () => ({
            ...state,
            formData: overwhelmedOverlayData,
        }),
        [state, overwhelmedOverlayData]
    );

    const memoizedActions = React.useMemo(
        () => ({
            showOverwhelmedOverlay: () => {
                if (p100IdsOverwhelmedOverlay) {
                    dispatch({ type: 'SHOW_OVERWHELMED_OVERLAY' });
                    trackIdsEvent({ action: 'Overlay view', label: 'Overlay view' });
                }
            },
            hideOverwhelmedOverlay: () => dispatch({ type: 'HIDE_OVERWHELMED_OVERLAY' }),
            showThankYouNote: () => p100IdsOverwhelmedOverlay && dispatch({ type: 'SHOW_THANK_YOU_NOTE' }),
            hideThankYouNote: () => dispatch({ type: 'HIDE_THANK_YOU_NOTE' }),
        }),
        [p100IdsOverwhelmedOverlay, trackIdsEvent]
    );

    if (!p100IdsOverwhelmedOverlay) {
        return <React.Fragment>{children}</React.Fragment>;
    }

    return (
        <OverwhelmedOverlayStateContext.Provider value={memoizedState}>
            <OverwhelmedOverlayDispatchContext.Provider value={memoizedActions}>
                {children}
            </OverwhelmedOverlayDispatchContext.Provider>
        </OverwhelmedOverlayStateContext.Provider>
    );
};

const useOverwhelmedOverlayState = () => React.useContext(OverwhelmedOverlayStateContext);

const useOverwhelmedOverlayDispatch = () => React.useContext(OverwhelmedOverlayDispatchContext);

export { OverwhelmedOverlayProvider, useOverwhelmedOverlayState, useOverwhelmedOverlayDispatch };
